import { NewLayerCommand } from "../commands/NewLayerCommand";
import { SetWorkPathCommand } from "../commands/SetWorkPathCommand";
import { screenToDoc } from "../render/Viewport";
import { createShapeLayer } from "../document/Layer";
import type { KeyModifiers, PointerSample, Tool, ToolContext } from "./Tool";
import type { AnchorPoint, PathModel } from "../document/PathModel";

export type PenMode = "pen" | "freeform" | "curvature" | "add-anchor" | "delete-anchor" | "convert-point";

/**
 * What the user is dragging during a pointer-move event.
 *  - "new-handle"      → user just clicked to add an anchor and is dragging out a brand-new handle pair
 *  - "edit-handle"     → user grabbed an existing handle and is reshaping it
 *  - "edit-anchor"     → user grabbed an existing anchor body and is moving the whole point
 */
export type PenDragKind = "new-handle" | "edit-handle" | "edit-anchor" | null;

export interface PenDragState {
  kind: PenDragKind;
  /** Index of the anchor being edited (for edit-handle / edit-anchor / new-handle). */
  anchorIndex: number;
  /** Which handle: "left" or "right" (for edit-handle). */
  handleSide?: "left" | "right";
  /** Whether Alt was held when the drag started — used to break symmetry. */
  altLatched: boolean;
}

export interface PenSession {
  points: AnchorPoint[];
  closed: boolean;
  isDragging: boolean;
  dragStart: { x: number; y: number } | null;
  /** Detailed drag state for handle / anchor editing. */
  drag?: PenDragState;
  /**
   * When true, the next pointerUp must not normalize the last anchor's
   * handles back to corner — used by add-anchor / convert / close / delete
   * operations that fire entirely on pointerDown.
   */
  suppressNextUpReset?: boolean;
}

const CLOSE_THRESHOLD = 8;
const ANCHOR_HIT_RADIUS = 8;
const HANDLE_HIT_RADIUS = 6;
const ON_PATH_HIT_RADIUS = 10;

/** Output mode for the Pen tool (Photoshop options bar). */
export type PenOutputMode = "shape" | "path" | "pixels";

export class PenTool implements Tool {
  readonly id = "pen";
  readonly shortcut = "P";

  mode: PenMode = "pen";

  /**
   * Auto Add/Delete: when true, clicking ON a path segment inserts a new
   * anchor; clicking ON an existing interior anchor removes it. Mirrors
   * Photoshop's default Pen tool options-bar behaviour. (#208)
   */
  autoAddDelete = true;

  /**
   * Rubber Band: when true, a dashed preview segment follows the cursor from
   * the last placed anchor to the current pointer position while drawing.
   * Photoshop shows this in the Pen options bar. (#208)
   */
  rubberBand = false;

  /**
   * Controls what Enter produces:
   *   "shape"  → NewLayerCommand (ShapeLayer carrying the bezier path)
   *   "path"   → SetWorkPathCommand (saves path to document workPaths)
   *   "pixels" → existing rasterize behaviour (handled in main.ts)
   */
  outputMode: PenOutputMode = "path";

  private session: PenSession | null = null;
  private pointerDownPos: { x: number; y: number } | null = null;
  private lastMovePos: { x: number; y: number } | null = null;
  private altWasDown = false;
  /** For freeform pen: track previous point for line drawing. */
  private freeformLast: { x: number; y: number } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    this.pointerDownPos = { x: s.x, y: s.y };
    this.lastMovePos = null;
    this.altWasDown = s.altKey;

    // Handle mode-specific actions
    if (this.mode === "add-anchor") {
      this.addAnchorPoint(ctx, p);
      return;
    }
    if (this.mode === "delete-anchor") {
      this.deleteAnchorPoint(ctx, p);
      return;
    }
    if (this.mode === "convert-point") {
      this.convertPoint(ctx, p, s);
      return;
    }

    // Ctrl/Cmd + click: cancel current path
    if (s.ctrlKey || s.metaKey) {
      if (this.session) {
        this.session = null;
        ctx.requestRender();
      }
      return;
    }

    // --- Editing existing anchors / handles (works in default Pen mode) ---
    if (this.session && this.session.points.length > 0) {
      // 1) Hit-test handles first (smaller, on top).
      const handleHit = this.findHandleAt(p);
      if (handleHit) {
        this.session.drag = {
          kind: "edit-handle",
          anchorIndex: handleHit.index,
          handleSide: handleHit.side,
          altLatched: s.altKey,
        };
        this.session.isDragging = false; // becomes true on first move
        return;
      }

      // 2) Hit-test existing anchors.
      const anchorIdx = this.findAnchorAt(p);
      if (anchorIdx >= 0) {
        // Alt+click on an INTERIOR anchor → remove it (Photoshop parity).
        // Alt+click on the LAST anchor → toggle smooth/corner (Convert Point).
        // Anchor deletion is gated on autoAddDelete (#208).
        if (s.altKey) {
          if (this.autoAddDelete && this.session.points.length > 2 &&
              anchorIdx !== 0 && anchorIdx !== this.session.points.length - 1) {
            this.session.points.splice(anchorIdx, 1);
            this.session.suppressNextUpReset = true;
            ctx.requestRender();
            return;
          }
          // Last anchor → convert type.
          if (anchorIdx === this.session.points.length - 1) {
            this.togglePointType(anchorIdx, p);
            this.session.suppressNextUpReset = true;
            ctx.requestRender();
            return;
          }
        }

        // Plain click on the FIRST anchor closes the path when ≥3 anchors.
        if (anchorIdx === 0 && this.session.points.length > 2) {
          this.session.closed = true;
          this.session.suppressNextUpReset = true;
          ctx.requestRender();
          return;
        }

        // Interior anchors → drag to reposition.
        // (First and last anchors are reserved for close/extend semantics.)
        if (anchorIdx !== 0 && anchorIdx !== this.session.points.length - 1) {
          this.session.drag = {
            kind: "edit-anchor",
            anchorIndex: anchorIdx,
            altLatched: s.altKey,
          };
          return;
        }
        // Fall through: clicking the first/last anchor with < 3 points
        // just adds another anchor at the click position (preserves existing
        // contract that the path keeps growing).
      }

      // 3) Hit-test on-path segments → add anchor in default mode (PS parity).
      //    Skip when too close to an existing anchor (handled above).
      //    Only active when autoAddDelete is enabled (#208).
      const segHit = this.autoAddDelete ? this.findSegmentHit(p) : null;
      if (segHit) {
        // Don't insert if the click is closer to an anchor than the segment line.
        const segA = this.session.points[segHit.index]!;
        const segB = this.session.points[(segHit.index + 1) % this.session.points.length]!;
        const dA = Math.hypot(segA.x - p.x, segA.y - p.y);
        const dB = Math.hypot(segB.x - p.x, segB.y - p.y);
        if (dA > ANCHOR_HIT_RADIUS && dB > ANCHOR_HIT_RADIUS) {
          const newPt: AnchorPoint = { x: p.x, y: p.y, type: "corner" };
          this.session.points.splice(segHit.index + 1, 0, newPt);
          this.session.suppressNextUpReset = true;
          ctx.requestRender();
          return;
        }
      }
    }

    if (!this.session) {
      if (this.mode === "freeform") {
        this.session = {
          points: [{ x: p.x, y: p.y, type: "corner" }],
          closed: false, isDragging: false, dragStart: null,
        };
        this.freeformLast = { x: p.x, y: p.y };
      } else {
        // Standard or curvature pen
        this.session = {
          points: [{ x: p.x, y: p.y, type: this.mode === "curvature" ? "smooth" : "corner" }],
          closed: false, isDragging: false, dragStart: null,
        };
      }
      // Mark a "new-handle" drag candidate so move events extrude a handle.
      this.session.drag = {
        kind: "new-handle",
        anchorIndex: 0,
        altLatched: s.altKey,
      };
      return;
    }

    // Check if clicking near the first point to close the path (fallback,
    // in case anchor hit-test missed because the path's first point lies
    // outside the current bounds — kept for backward compatibility).
    const first = this.session.points[0];
    if (first && this.session.points.length > 2 &&
        Math.hypot(p.x - first.x, p.y - first.y) < CLOSE_THRESHOLD) {
      this.session.closed = true;
      ctx.requestRender();
      return;
    }

    // Add a new anchor point
    if (this.mode === "curvature") {
      // Curvature pen: auto-generates smooth curves
      this.addCurvaturePoint(p);
      // Mark drag state for newly added curvature point
      this.session.drag = {
        kind: "new-handle",
        anchorIndex: this.session.points.length - 1,
        altLatched: s.altKey,
      };
    } else if (this.mode === "freeform") {
      // Freeform: handled in onPointerMove
      this.session.points.push({ x: p.x, y: p.y, type: "corner" });
      this.freeformLast = { x: p.x, y: p.y };
    } else {
      this.session.points.push({ x: p.x, y: p.y, type: "corner" });
      this.session.drag = {
        kind: "new-handle",
        anchorIndex: this.session.points.length - 1,
        altLatched: s.altKey,
      };
    }
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    this.lastMovePos = { x: s.x, y: s.y };

    // Freeform pen: add points as user drags
    if (this.mode === "freeform" && this.session && !this.session.closed) {
      const p = screenToDoc(ctx.viewport, s.x, s.y);
      if (this.freeformLast) {
        const dist = Math.hypot(p.x - this.freeformLast.x, p.y - this.freeformLast.y);
        if (dist > 2) {
          this.session.points.push({ x: p.x, y: p.y, type: "corner" });
          this.freeformLast = { x: p.x, y: p.y };
          ctx.requestRender();
          return;
        }
      }
    }

    if (!this.session || this.session.closed) {
      ctx.requestRender();
      return;
    }

    const docPos = screenToDoc(ctx.viewport, s.x, s.y);
    const drag = this.session.drag;

    // --- Drag an existing handle ---
    if (drag?.kind === "edit-handle") {
      const pt = this.session.points[drag.anchorIndex];
      if (!pt) { ctx.requestRender(); return; }
      this.session.isDragging = true;
      if (drag.handleSide === "right") {
        pt.rightDirection = { x: docPos.x, y: docPos.y };
        if (!s.altKey && !drag.altLatched && pt.type !== "corner") {
          // Symmetric mirror.
          pt.leftDirection = { x: pt.x - (docPos.x - pt.x), y: pt.y - (docPos.y - pt.y) };
        } else {
          // Alt held → break smoothness symmetry.
          pt.type = "corner";
        }
      } else {
        pt.leftDirection = { x: docPos.x, y: docPos.y };
        if (!s.altKey && !drag.altLatched && pt.type !== "corner") {
          pt.rightDirection = { x: pt.x - (docPos.x - pt.x), y: pt.y - (docPos.y - pt.y) };
        } else {
          pt.type = "corner";
        }
      }
      ctx.requestRender();
      return;
    }

    // --- Drag an existing anchor body ---
    if (drag?.kind === "edit-anchor") {
      const pt = this.session.points[drag.anchorIndex];
      if (!pt) { ctx.requestRender(); return; }
      this.session.isDragging = true;
      const dx = docPos.x - pt.x;
      const dy = docPos.y - pt.y;
      pt.x = docPos.x;
      pt.y = docPos.y;
      // Move handles by the same delta so the curve glides with the anchor.
      if (pt.leftDirection) {
        pt.leftDirection.x += dx;
        pt.leftDirection.y += dy;
      }
      if (pt.rightDirection) {
        pt.rightDirection.x += dx;
        pt.rightDirection.y += dy;
      }
      ctx.requestRender();
      return;
    }

    // --- New-handle drag: extruding handles from the most-recent anchor ---
    const lastPt = this.session.points[this.session.points.length - 1];
    if (!lastPt) {
      ctx.requestRender();
      return;
    }

    if (s.altKey && drag?.kind === "new-handle") {
      if (lastPt.type !== "corner") {
        lastPt.rightDirection = { x: docPos.x, y: docPos.y };
      }
      ctx.requestRender();
      return;
    }

    const downPos = this.pointerDownPos;
    if (downPos) {
      const dragDist = Math.hypot(s.x - downPos.x, s.y - downPos.y);
      if (dragDist > 3) {
        this.session.isDragging = true;
        this.session.dragStart = { x: lastPt.x, y: lastPt.y };

        lastPt.rightDirection = { x: docPos.x, y: docPos.y };
        lastPt.leftDirection = {
          x: lastPt.x - (docPos.x - lastPt.x),
          y: lastPt.y - (docPos.y - lastPt.y),
        };
        lastPt.type = "smooth";
      }
    }

    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext, _s: PointerSample): void {
    this.pointerDownPos = null;
    if (!this.session) return;

    const drag = this.session.drag;

    if (drag && drag.kind !== "new-handle") {
      // Editing finished — clear and return.
      delete this.session.drag;
      this.session.isDragging = false;
      ctx.requestRender();
      return;
    }

    if (this.session.closed) {
      this.session.isDragging = false;
      delete this.session.drag;
      this.session.suppressNextUpReset = false;
      return;
    }

    if (this.session.suppressNextUpReset) {
      this.session.suppressNextUpReset = false;
      this.session.isDragging = false;
      delete this.session.drag;
      this.session.dragStart = null;
      return;
    }

    const lastPt = this.session.points[this.session.points.length - 1];
    if (!lastPt) return;

    if (!this.session.isDragging && this.mode !== "freeform" && this.mode !== "curvature") {
      lastPt.type = "corner";
      delete lastPt.leftDirection;
      delete lastPt.rightDirection;
    }

    this.session.isDragging = false;
    this.session.dragStart = null;
    delete this.session.drag;
    ctx.requestRender();
  }

  onKeyDown(ctx: ToolContext, key: string, modifiers?: KeyModifiers): void {
    if (key === "Enter" && this.session) {
      const session = this.session;
      if (session.points.length >= 2) {
        if (this.outputMode === "shape") {
          // Build a PathModel from the current session and create a ShapeLayer
          const path: PathModel = {
            subpaths: [{
              points: session.points.map((pt) => ({ ...pt })),
              closed: session.closed,
            }],
          };
          const shapeLayer = createShapeLayer({
            path,
            fillColor: ctx.fgColor
              ? { r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b, a: 1 }
              : { r: 100, g: 150, b: 255, a: 1 },
          });
          ctx.emit(new NewLayerCommand(shapeLayer));
          // Clear session so main.ts rasterize path is skipped
          this.session = null;
          ctx.requestRender();
          return;
        }

        if (this.outputMode === "path") {
          // Save as work path in the document
          const path: PathModel = {
            subpaths: [{
              points: session.points.map((pt) => ({ ...pt })),
              closed: session.closed,
            }],
          };
          ctx.emit(new SetWorkPathCommand(path));
          // Clear session so main.ts rasterize path is skipped
          this.session = null;
          ctx.requestRender();
          return;
        }

        // outputMode === "pixels": fall through — main.ts calls rasterizePathOnLayer
        // Leave session intact so main.ts can read it via getSession()
      }
      ctx.requestRender();
      return;
    }
    if (key === "Escape" && this.session) {
      this.session = null;
      ctx.requestRender();
    }
  }

  getSession(): PenSession | null {
    return this.session;
  }

  /** Replace the active session — used by tests and external editors. */
  setSession(session: PenSession | null): void {
    this.session = session;
  }

  getCursorDocPos(): { x: number; y: number } | null {
    if (!this.lastMovePos || !this.session) return null;
    return null;
  }

  // --- Hit testing helpers ---

  /** Returns the index of the anchor whose body lies under `p`, or -1. */
  private findAnchorAt(p: { x: number; y: number }): number {
    if (!this.session) return -1;
    const pts = this.session.points;
    for (let i = 0; i < pts.length; i++) {
      if (Math.hypot(pts[i]!.x - p.x, pts[i]!.y - p.y) <= ANCHOR_HIT_RADIUS) return i;
    }
    return -1;
  }

  /** Returns the (index, side) of the handle under `p`, or null. */
  private findHandleAt(p: { x: number; y: number }): { index: number; side: "left" | "right" } | null {
    if (!this.session) return null;
    const pts = this.session.points;
    for (let i = 0; i < pts.length; i++) {
      const pt = pts[i]!;
      if (pt.rightDirection &&
          Math.hypot(pt.rightDirection.x - p.x, pt.rightDirection.y - p.y) <= HANDLE_HIT_RADIUS) {
        return { index: i, side: "right" };
      }
      if (pt.leftDirection &&
          Math.hypot(pt.leftDirection.x - p.x, pt.leftDirection.y - p.y) <= HANDLE_HIT_RADIUS) {
        return { index: i, side: "left" };
      }
    }
    return null;
  }

  /** Returns the index of the segment START that lies near `p`, or null. */
  private findSegmentHit(p: { x: number; y: number }): { index: number } | null {
    if (!this.session) return null;
    const pts = this.session.points;
    const n = pts.length;
    if (n < 2) return null;
    const lastIdx = this.session.closed ? n : n - 1;
    let best = -1;
    let bestDist = Infinity;
    for (let i = 0; i < lastIdx; i++) {
      const a = pts[i]!;
      const b = pts[(i + 1) % n]!;
      const d = distToSegment(p, a, b);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    if (best >= 0 && bestDist <= ON_PATH_HIT_RADIUS) return { index: best };
    return null;
  }

  /** Toggle the given anchor between corner and smooth. Used by Alt+click on last point. */
  private togglePointType(idx: number, p: { x: number; y: number }): void {
    if (!this.session) return;
    const pt = this.session.points[idx];
    if (!pt) return;
    if (pt.type === "corner") {
      // Build symmetric handles tangent to the click position.
      pt.rightDirection = { x: p.x, y: p.y };
      pt.leftDirection = { x: pt.x - (p.x - pt.x), y: pt.y - (p.y - pt.y) };
      pt.type = "smooth";
    } else {
      delete pt.leftDirection;
      delete pt.rightDirection;
      pt.type = "corner";
    }
  }

  // --- Mode-specific helpers ---

  private addCurvaturePoint(p: { x: number; y: number }): void {
    if (!this.session) return;
    const pts = this.session.points;
    if (pts.length === 1) {
      pts.push({ x: p.x, y: p.y, type: "smooth" });
      return;
    }
    // For curvature pen, compute handles based on previous and next points
    const prev = pts[pts.length - 2]!;
    const curr = pts[pts.length - 1]!;
    // Set handles for the previous point
    const midX = (prev.x + curr.x) / 2;
    const midY = (prev.y + curr.y) / 2;
    prev.rightDirection = { x: midX, y: midY };
    prev.leftDirection = {
      x: prev.x - (midX - prev.x),
      y: prev.y - (midY - prev.y),
    };
    prev.type = "smooth";

    // Add new point
    pts.push({ x: p.x, y: p.y, type: "smooth" });
  }

  private addAnchorPoint(ctx: ToolContext, p: { x: number; y: number }): void {
    if (!this.session) return;
    // Find the segment closest to the click point and insert a new anchor
    const pts = this.session.points;
    let bestDist = Infinity;
    let bestIdx = -1;
    for (let i = 0; i < pts.length - 1; i++) {
      const d = distToSegment(p, pts[i]!, pts[i + 1]!);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    if (bestIdx >= 0 && bestDist < 10) {
      const newPt: AnchorPoint = { x: p.x, y: p.y, type: "corner" };
      pts.splice(bestIdx + 1, 0, newPt);
      ctx.requestRender();
    }
  }

  private deleteAnchorPoint(ctx: ToolContext, p: { x: number; y: number }): void {
    if (!this.session) return;
    const pts = this.session.points;
    for (let i = pts.length - 1; i >= 0; i--) {
      if (Math.hypot(pts[i]!.x - p.x, pts[i]!.y - p.y) < 8) {
        if (pts.length > 2) {
          pts.splice(i, 1);
          ctx.requestRender();
        }
        return;
      }
    }
  }

  private convertPoint(ctx: ToolContext, p: { x: number; y: number }, s: PointerSample): void {
    if (!this.session) return;
    const pts = this.session.points;
    for (let i = pts.length - 1; i >= 0; i--) {
      if (Math.hypot(pts[i]!.x - p.x, pts[i]!.y - p.y) < 8) {
        const pt = pts[i]!;
        if (pt.type === "corner") {
          // Convert to smooth: create symmetric handles
          const docPos = screenToDoc(ctx.viewport, s.x, s.y);
          pt.rightDirection = { x: docPos.x, y: docPos.y };
          pt.leftDirection = {
            x: pt.x - (docPos.x - pt.x),
            y: pt.y - (docPos.y - pt.y),
          };
          pt.type = "smooth";
        } else {
          // Convert to corner: remove handles
          delete pt.leftDirection;
          delete pt.rightDirection;
          pt.type = "corner";
        }
        ctx.requestRender();
        return;
      }
    }
  }
}

function distToSegment(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
